import React, { useState, useEffect, useCallback } from 'react';
import { employeeAPI, PersonalUseAPI } from '../../../services/api.ts';
import { Layout } from '../../../components/Layout.tsx';
import { Toast } from '../../../components/Toast.tsx';
import FilterModal from '../../../components/FilterModal.tsx';
import dayjs from 'dayjs';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import EmployeeInfoModal from '../../../components/Modal/EmployeeInfo.tsx';

import { useNavigate } from 'react-router-dom';

export const PersonalUse = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [filteredProjects, setFilteredProjects] = useState<any[]>([]);
  const [filterByGroup, setFilterByGroup] = useState('');
  const [filterDataByGroup, setFilterDataByGroup] = useState<any[]>([]);
  const [checkboxStates, setCheckboxStates] = useState('');
  const [dateFilter, setDateFilter] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState<any>(null);

  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const checkboxProps = [
    { id: 'employee', label: 'Group of Employee' },
    { id: 'company', label: 'Group of Company' },
  ];

  const handleApply = async (
    startDate: string,
    endDate: string,
    checkboxState: any
  ) => {
    if (checkboxState) {
      setFilterByGroup(checkboxState);
      setIsModalOpen(false);
    }
    try {
      setLoading(true);

      if (startDate && endDate) {
        const response = await PersonalUseAPI.getPersonalUseAPIFilter(
          page,
          50,
          startDate,
          endDate
        );
        if (response.data) {
          setProjects(response.data as unknown as any[]);
        }
        setDateFilter(true);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.totalPersonalUses || 0);
        setIsModalOpen(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch personal use records record');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await PersonalUseAPI.getAllPersonalUse(page);
      if (response?.data) {
        setProjects(response.data as unknown as any[]);
        // console.log(response.data);

        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.totalPersonalUses || 0);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sales data');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchProjects();
  }, [page, fetchProjects]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProjects(projects);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filteredData = projects.filter((project) => {
      const id = project?.id?.toString()?.toLowerCase() || '';
      const companyName = project?.company?.name?.toLowerCase() || '';
      const employeeName = project?.employee?.name?.toLowerCase() || '';

      return (
        id.includes(lowerSearch) ||
        companyName.includes(lowerSearch) ||
        employeeName.includes(lowerSearch)
      );
    });
    setFilteredProjects(filteredData);
  }, [searchTerm, projects]);

  useEffect(() => {
    if (!filterByGroup) return;
    let groupedProjects: any[] = [];

    // Group by Employee, Territory, Company, or Customer
    if (filterByGroup === 'employee') {
      groupedProjects = projects.reduce((acc: any[], project) => {
        const employeeName = project.employee?.name || 'Unknown';
        const existingGroup = acc.find(
          (group) => group.employeeName === employeeName
        );

        if (existingGroup) {
          existingGroup.projects.push(project);
        } else {
          acc.push({ employeeName, projects: [project] });
        }

        return acc;
      }, []);
    }

    if (filterByGroup === 'company') {
      groupedProjects = projects.reduce((acc: any[], project) => {
        const companyName = project.company?.name || 'Unknown';
        const existingGroup = acc.find(
          (group) => group.companyName === companyName
        );

        if (existingGroup) {
          existingGroup.projects.push(project);
        } else {
          acc.push({ companyName, projects: [project] });
        }

        return acc;
      }, []);
    }

    setFilterDataByGroup(groupedProjects);
  }, [filterByGroup, projects]);

  const resetFilter = () => {
    setFilterByGroup('');
    setFilterDataByGroup([]);
    setSearchTerm('');
    setCheckboxStates('');
    setDateFilter(false);
    fetchProjects();
  };

  useEffect(() => {
    if (selectedEmployee) {
      setShowEmployeeDetails(true);
      employeeAPI.details(selectedEmployee).then((res) => {
        if (res.data) {
          setEmployeeDetails(res.data);
        }
      });
      return () => {
        setShowEmployeeDetails(false);
        setEmployeeDetails(null);
      };
    }
  }, [selectedEmployee]);

  return (
    <Layout>
      <div className="sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 items-center gap-4">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Personal Use Records
          </h1>
          <div className="col-span-2 flex gap-3">
            <input
              type="search"
              className="w-full p-2 border rounded shadow-md dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              placeholder="Search Personal use records"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => {
                setFilterByGroup('');
                setFilterDataByGroup([]);
              }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/fleet/personal-use/cross-match')}
                className="px-5 py-3 bg-indigo-600 font-medium shadow-sm hover:bg-indigo-700 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 text-white rounded-md transition-colors"
              >
                Cross-Match
              </button>
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-5 py-3 bg-primary font-medium shadow-sm hover:bg-primary-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 text-white rounded-md "
              >
                Filter
              </button>
              {(filterDataByGroup.length > 0 || dateFilter) && (
                <button
                  onClick={resetFilter}
                  className="px-5 py-3 bg-red-400 font-medium shadow-sm hover:bg-red-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-100 focus:ring-offset-2 text-white rounded-md "
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mt-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400 dark:text-green-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                  {successMessage}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    type="button"
                    onClick={() => setSuccessMessage('')}
                    className="inline-flex rounded-md p-1.5 text-green-500 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400 dark:text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  {error}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    type="button"
                    onClick={() => setError('')}
                    className="inline-flex rounded-md p-1.5 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            <table className="w-full mt-4 border-collapse border border-gray-300 dark:border-gray-700 md:rounded-lg dark:bg-gray-800">
              <thead>
                <tr className="bg-gray-200 dark:bg-gray-700">
                  <th className="border dark:border-gray-700 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    ID
                  </th>
                  <th className="border dark:border-gray-700 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Name
                  </th>
                  <th className="border dark:border-gray-700 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Employee
                  </th>

                  <th className="border dark:border-gray-700 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Odoo Status
                  </th>
                  <th className="border dark:border-gray-700 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Company
                  </th>
                  <th className="border dark:border-gray-700 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Total Travelled (KM)
                  </th>
                  <th className="border dark:border-gray-700 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Travelled Amount (PKR)
                  </th>
                  <th className="border dark:border-gray-700 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">
                    Created At
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.length > 0 &&
                  filterDataByGroup.length === 0 && (
                    <>
                      {filteredProjects.map((project) => (
                        <tr
                          key={project._id}
                          className="border-b dark:border-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <td className="px-3 py-4 text-sm">
                            {project.id.toString()}
                          </td>
                          <td className="px-3 py-4 text-sm">{project.name}</td>
                          <td className="px-3 py-4 text-sm">
                            {project.employee.name}{' '}
                            <InformationCircleIcon
                              className="h-4 w-4 text-gray-500 dark:text-gray-400 inline-block cursor-pointer"
                              onClick={() =>
                                setSelectedEmployee(project.employee.id)
                              }
                            />
                          </td>
                          <td className="px-3 py-4 text-sm">
                            {project.odooStatus}
                          </td>
                          <td className="px-3 py-4 text-sm">
                            {project.company.name}
                          </td>
                          <td className="px-3 py-4 text-sm">
                            {project?.personal_travel} KM
                          </td>
                          <td className="px-3 py-4 text-sm">
                            {project?.personal_travel_amount?.toString()} KM
                          </td>
                          <td className="px-3 py-4 text-sm">
                            {dayjs(project.createdAt).format(
                              'DD/MM/YYYY HH:mm:ss'
                            )}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}

                {filterDataByGroup.length > 0 && (
                  <>
                    {filterDataByGroup.map((group) => (
                      <React.Fragment
                        key={group.employeeName || group.companyName}
                      >
                        <tr>
                          <td
                            colSpan={7}
                            className="text-gray-900 dark:text-white font-semibold px-3 py-3 bg-gray-50 dark:bg-gray-700"
                          >
                            {group.employeeName || group.companyName}
                          </td>
                        </tr>
                        {group.projects.map((project: any) => (
                          <tr
                            key={project._id}
                            className="border-b dark:border-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <td className="px-3 py-4 text-sm">
                              {project.id.toString()}
                            </td>
                            <td className="px-3 py-4 text-sm">
                              {project.name}
                            </td>
                            <td className="px-3 py-4 text-sm">
                              {project.employee.name}{' '}
                              <InformationCircleIcon
                                className="h-4 w-4 text-gray-500 dark:text-gray-400 inline-block cursor-pointer"
                                onClick={() =>
                                  setSelectedEmployee(project.employee.id)
                                }
                              />
                            </td>
                            <td className="px-3 py-4 text-sm">
                              {project.odooStatus}
                            </td>
                            <td className="px-3 py-4 text-sm">
                              {project.company.name}
                            </td>
                            <td className="px-3 py-4 text-sm">
                              {project?.personal_travel} KM
                            </td>
                            <td className="px-3 py-4 text-sm">
                              {project?.personal_travel_amount?.toString()} KM
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </>
                )}

                {filteredProjects.length === 0 &&
                  filterDataByGroup.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center p-4 text-gray-500 dark:text-gray-400"
                      >
                        No Personal use records found
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>

            {/* Pagination - Only show when there are projects */}
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{' '}
                    <span className="font-medium">
                      {Math.min((page - 1) * 50 + 1, totalItems)}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(page * 50, totalItems)}
                    </span>{' '}
                    of <span className="font-medium">{totalItems}</span> results
                  </p>
                </div>
                <div>
                  <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <FilterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onApply={handleApply}
        setCheckboxStates={setCheckboxStates}
        checkboxStates={checkboxStates}
        checkboxProps={checkboxProps}
      />

      {showEmployeeDetails && employeeDetails && (
        <EmployeeInfoModal
          employee={employeeDetails}
          onClose={() => setShowEmployeeDetails(false)}
        />
      )}
    </Layout>
  );
};
